/**
 * AdminProducts — iter28 rewrite.
 *
 * Key fixes:
 *   • File uploads go through /api/admin/media/upload (GridFS) instead of
 *     embedding base64 dataURLs in the product document.  Saves us from
 *     DocumentTooLarge errors and keeps the products collection small.
 *   • Per-variant image management — every size variant has its own
 *     uploadable image_url which the storefront swaps in when the user
 *     selects that variant.
 *   • Edit modal is a flex column with a non-scrollable sticky header +
 *     footer and a scrollable middle so the top of the form is always
 *     reachable on small screens.
 */
import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../../lib/api";
import { Plus, Edit2, Trash2, X, Upload, Trash, Cloud, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { uploadMedia } from "../../lib/uploadMedia";
import CloudinaryImagePicker from "../../components/admin/CloudinaryImagePicker";

import { useCategories } from "../../hooks/useCategories";

const CATS_FALLBACK = ["Pickles", "Culinary Pastes", "Spices", "Masalas", "Traditional Powders", "Flours", "Wood Pressed Oils", "Combos"];
const EMPTY = { name: "", category: "Pickles", price: 0, mrp: null, description: "", image_url: "", images: [], variants: [], combo_items: [], slug: "", in_stock: true, is_bestseller: false, weight: "" };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("All");
  const [uploadBusy, setUploadBusy] = useState(false);
  // iter40: Cloudinary picker — target tells the picker whether the
  // chosen URL should go to the main product image or a specific variant.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState({ kind: "main", index: -1 });

  const openPicker = (target) => { setPickerTarget(target); setPickerOpen(true); };
  const onPickFromCloudinary = (url) => {
    if (pickerTarget.kind === "main") {
      setEditing((p) => ({ ...p, image_url: url }));
    } else if (pickerTarget.kind === "variant") {
      setEditing((p) => {
        const arr = [...(p.variants || [])];
        arr[pickerTarget.index] = { ...arr[pickerTarget.index], image_url: url };
        return { ...p, variants: arr };
      });
    }
  };

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const onSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing };
      if (payload.variants?.length) {
        payload.price = Math.min(...payload.variants.map((v) => parseFloat(v.price) || 0));
      }
      if (payload.product_id) {
        await api.put(`/products/${payload.product_id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const onToggleStock = async (p) => {
    try { await api.put(`/products/${p.product_id}`, { in_stock: !p.in_stock }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // iter65 — manual product ordering. Mirrors the Hero Carousel reorder
  // UX: each row carries Up / Down arrows; clicking either swaps the
  // product with its neighbor in the SAME category.  The backend
  // re-sequences the whole category on every move so sort_index stays
  // a contiguous 0..N-1 list (no drift, no duplicate indices).  We
  // optimistically update local state so the UI feels instant, then
  // reconcile with the server response.
  const [movingId, setMovingId] = useState("");
  const move = async (product, direction) => {
    setMovingId(product.product_id);
    try {
      const r = await api.post(
        `/admin/products/${product.product_id}/reorder`,
        null,
        { params: { direction } },
      );
      // Merge the updated category list back into the master products
      // array — every other category keeps its existing sort_index.
      const updated = r.data?.products || [];
      const byId = new Map(updated.map((p) => [p.product_id, p]));
      setProducts((prev) => prev.map((p) => byId.get(p.product_id) || p));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMovingId("");
    }
  };

  // Single-file upload helper that surfaces nice errors.
  const doUpload = async (file, onSuccess) => {
    if (!file) return;
    setUploadBusy(true);
    try {
      const url = await uploadMedia(file);
      onSuccess(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setUploadBusy(false);
    }
  };

  const onUploadMain = (e) => doUpload(e.target.files?.[0], (url) => setEditing((p) => ({ ...p, image_url: url })));
  const onUploadExtra = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    doUpload(f, (url) => setEditing((p) => ({ ...p, images: [...(p.images || []), url] })));
    e.target.value = "";
  };
  const onUploadVariant = (i, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    doUpload(f, (url) => {
      setEditing((p) => {
        const arr = [...p.variants];
        arr[i] = { ...arr[i], image_url: url };
        return { ...p, variants: arr };
      });
    });
    e.target.value = "";
  };

  const removeExtraImage = (idx) => setEditing({ ...editing, images: editing.images.filter((_, i) => i !== idx) });
  const clearMainImage = () => setEditing({ ...editing, image_url: "" });
  const clearVariantImage = (i) => {
    const arr = [...editing.variants]; arr[i] = { ...arr[i], image_url: "" };
    setEditing({ ...editing, variants: arr });
  };

  const addVariant = () => setEditing({ ...editing, variants: [...(editing.variants || []), { label: "", price: 0, available: true, image_url: "" }] });
  const updateVariant = (i, patch) => {
    const arr = [...editing.variants]; arr[i] = { ...arr[i], ...patch };
    setEditing({ ...editing, variants: arr });
  };
  const removeVariant = (i) => setEditing({ ...editing, variants: editing.variants.filter((_, idx) => idx !== i) });

  // iter70 — Combo items editor (only shown when category === "Combos").
  // Every combo item links to a real catalog product (SKU-based).  Admin
  // can add, remove, reorder and edit qty / individual_mrp / variant_label.
  const nonComboProducts = useMemo(
    () => products.filter((p) => p.category !== "Combos"),
    [products]
  );
  const productById = useMemo(
    () => Object.fromEntries(products.map((p) => [p.product_id, p])),
    [products]
  );

  const addComboItem = () => {
    // Default to the first non-combo product's first variant so the row
    // is never empty / invalid on creation.
    const first = nonComboProducts[0];
    if (!first) { toast.error("Add regular products first, then compose combos."); return; }
    const firstVariant = (first.variants || [])[0];
    setEditing({
      ...editing,
      combo_items: [
        ...(editing.combo_items || []),
        {
          product_id: first.product_id,
          variant_label: firstVariant?.label || "",
          quantity: 1,
          individual_mrp: firstVariant?.price ?? first.price ?? 0,
        },
      ],
    });
  };
  const updateComboItem = (i, patch) => {
    const arr = [...(editing.combo_items || [])];
    const cur = { ...arr[i], ...patch };
    // If product changed, snap the variant to the new product's first variant.
    if (patch.product_id && patch.product_id !== arr[i].product_id) {
      const np = productById[patch.product_id];
      const nv = (np?.variants || [])[0];
      cur.variant_label = nv?.label || "";
      cur.individual_mrp = nv?.price ?? np?.price ?? 0;
    }
    arr[i] = cur;
    setEditing({ ...editing, combo_items: arr });
  };
  const removeComboItem = (i) => setEditing({ ...editing, combo_items: (editing.combo_items || []).filter((_, idx) => idx !== i) });
  const moveComboItem = (i, dir) => {
    const arr = [...(editing.combo_items || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setEditing({ ...editing, combo_items: arr });
  };

  // iter76 — live categories from Admin → Categories.
  const liveCategories = useCategories();
  const CATS = useMemo(
    () => (liveCategories.length ? liveCategories.map((c) => c.name) : CATS_FALLBACK),
    [liveCategories]
  );

  // iter65 — products mutate locally on reorder so we MUST sort the
  // filtered view by (category_rank, sort_index, name) to keep the
  // table reflecting the canonical order without a network round-trip.
  const CAT_RANK = useMemo(() => Object.fromEntries(CATS.map((c, i) => [c, i])), [CATS]);
  const sortedAll = useMemo(() => {
    const copy = [...products];
    copy.sort((a, b) =>
      (CAT_RANK[a.category] ?? 999) - (CAT_RANK[b.category] ?? 999) ||
      (a.sort_index ?? 999) - (b.sort_index ?? 999) ||
      a.name.localeCompare(b.name)
    );
    return copy;
  }, [products, CAT_RANK]);
  const filtered = filter === "All" ? sortedAll : sortedAll.filter((p) => p.category === filter);

  // iter65 — pre-compute the in-category index for every visible product
  // so each row's Up/Down arrows can disable themselves at the boundary
  // (first row's Up + last row's Down).  Using `useMemo` keeps the
  // computation O(N) per render instead of O(N²) inside the table loop.
  const categoryOrder = useMemo(() => {
    const order = new Map(); // product_id -> { index, total }
    const grouped = new Map();
    for (const p of products) {
      if (!grouped.has(p.category)) grouped.set(p.category, []);
      grouped.get(p.category).push(p);
    }
    for (const list of grouped.values()) {
      list.sort((a, b) => (a.sort_index ?? 999) - (b.sort_index ?? 999) || a.name.localeCompare(b.name));
      list.forEach((p, i) => order.set(p.product_id, { index: i, total: list.length }));
    }
    return order;
  }, [products]);

  const combosCount = useMemo(() => products.filter((p) => p.category === "Combos").length, [products]);
  const [seedingCombos, setSeedingCombos] = useState(false);
  const onSeedCombos = async () => {
    if (!window.confirm("Seed the 19 initial combos into the database? This is safe to run multiple times — existing combos will only be updated, not duplicated.")) return;
    setSeedingCombos(true);
    try {
      const r = await api.post("/admin/seed-combos");
      const d = r.data || {};
      toast.success(`Combos: ${d.created} created, ${d.updated} updated, ${d.skipped} skipped.`);
      if ((d.skipped_details || []).length) {
        console.warn("[seed-combos] skipped:", d.skipped_details);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Combo seed failed"));
    } finally {
      setSeedingCombos(false);
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap justify-between items-center mb-8 gap-3">
        <h1 className="font-serif text-4xl text-brand-dark">Products <span className="text-base text-brand-muted ml-2">({products.length})</span></h1>
        <div className="flex items-center gap-2">
          {/* iter70 — one-click self-serve seed for the 19 initial combos.
               Visible only when the Combos category is empty so it never
               nags an admin who has already populated things. */}
          {combosCount === 0 && (
            <button
              type="button"
              onClick={onSeedCombos}
              disabled={seedingCombos}
              className="text-xs uppercase tracking-wider px-3 py-2 rounded-full border border-brand-chili text-brand-chili hover:bg-brand-chili hover:text-white transition disabled:opacity-60"
              data-testid="admin-seed-combos"
            >
              {seedingCombos ? "Seeding…" : "Seed 19 Combos"}
            </button>
          )}
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="admin-add-product"><Plus className="w-4 h-4 mr-1" />New product</button>
        </div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {["All", ...CATS].map((c) => (
          <button key={c} onClick={() => setFilter(c)} className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-full ${filter === c ? "bg-brand-dark text-white" : "bg-brand-subtle"}`} data-testid={`admin-filter-${c.toLowerCase()}`}>{c}</button>
        ))}
      </div>

      <div className="bg-white border border-brand-line rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-subtle text-xs uppercase tracking-wider text-brand-muted">
            <tr>
              <th className="text-left p-3 w-20">Order</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">From</th>
              <th className="text-left p-3">Variants</th>
              <th className="text-left p-3">Stock</th>
              <th className="text-left p-3">Best</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const meta = categoryOrder.get(p.product_id) || { index: 0, total: 1 };
              const isFirst = meta.index === 0;
              const isLast = meta.index === meta.total - 1;
              const isBusy = movingId === p.product_id;
              return (
              <tr key={p.product_id} className="border-t border-brand-line" data-testid={`admin-product-row-${p.product_id}`}>
                <td className="p-2 align-middle whitespace-nowrap">
                  {/* iter65 — manual reorder controls (mirrors Hero Carousel arrows) */}
                  <div className="inline-flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => move(p, "up")}
                      disabled={isFirst || isBusy}
                      title={isFirst ? "Already at top" : "Move up"}
                      aria-label="Move product up"
                      data-testid={`product-up-${p.product_id}`}
                      className="p-1.5 rounded-md text-brand-muted hover:text-brand-chili hover:bg-brand-subtle disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-brand-muted transition-colors"
                    >
                      <ArrowUp className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(p, "down")}
                      disabled={isLast || isBusy}
                      title={isLast ? "Already at bottom" : "Move down"}
                      aria-label="Move product down"
                      data-testid={`product-down-${p.product_id}`}
                      className="p-1.5 rounded-md text-brand-muted hover:text-brand-chili hover:bg-brand-subtle disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-brand-muted transition-colors"
                    >
                      <ArrowDown className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-brand-muted mt-0.5 text-center" data-testid={`product-order-${p.product_id}`}>{meta.index + 1}/{meta.total}</div>
                </td>
                <td className="p-3 flex items-center gap-3"><img src={p.image_url} className="w-10 h-10 object-contain bg-brand-cream rounded-lg p-1" alt="" /><span className="font-medium">{p.name}</span></td>
                <td className="p-3 text-brand-muted">{p.category}</td>
                <td className="p-3 font-medium">₹{p.price}</td>
                <td className="p-3 text-brand-muted text-xs">{p.variants?.length ? p.variants.map(v => v.label).join(", ") : "—"}</td>
                <td className="p-3"><button onClick={() => onToggleStock(p)} className={`text-xs px-2.5 py-1 rounded-full ${p.in_stock ? "bg-brand-success/10 text-brand-success" : "bg-brand-subtle text-brand-muted"}`} data-testid={`toggle-stock-${p.product_id}`}>{p.in_stock ? "In stock" : "Out"}</button></td>
                <td className="p-3">{p.is_bestseller && <span className="badge-bestseller">★</span>}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing({ ...p })} className="text-brand-muted hover:text-brand-chili p-2" data-testid={`edit-product-${p.product_id}`}><Edit2 className="w-4 h-4" strokeWidth={1.5} /></button>
                  <button onClick={() => onDelete(p.product_id)} className="text-brand-muted hover:text-brand-chili p-2" data-testid={`delete-product-${p.product_id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-brand-dark/60 flex items-center justify-center p-2 sm:p-4" data-testid="product-modal">
          {/* Flex-column modal — only the inner body scrolls so the title
              header and the Save / Cancel footer stay reachable. */}
          <div className="bg-white rounded-2xl w-full max-w-2xl flex flex-col max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)]">
            <div className="flex justify-between items-center px-6 py-4 border-b border-brand-line shrink-0">
              <h3 className="font-serif text-2xl text-brand-dark">{editing.product_id ? "Edit" : "New"} product</h3>
              <button onClick={() => setEditing(null)} className="p-2"><X className="w-5 h-5" /></button>
            </div>

            <form id="product-form" onSubmit={onSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-y-auto px-6 py-6 flex-1 min-h-0">
              <div className="sm:col-span-2"><label className="label-field">Name</label><input className="input-field" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required data-testid="modal-name" /></div>
              <div><label className="label-field">Category</label>
                <select className="input-field" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="modal-category">
                  {CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div><label className="label-field">Base Price (₹)</label><input type="number" step="1" className="input-field" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })} data-testid="modal-price" /></div>
              {/* iter68 — MRP field.  Optional for every other category
                   (leave blank to skip the strike-through pricing on
                   the storefront), REQUIRED for Combos where it drives
                   the "MRP struck / You Save ₹X" badge.  When Combos
                   is selected the label makes it mandatory and the
                   variants section (below) is hidden. */}
              <div>
                <label className="label-field">
                  MRP (₹) <span className="text-brand-muted text-xs">{editing.category === "Combos" ? "· required" : "· optional"}</span>
                </label>
                <input
                  type="number"
                  step="1"
                  className="input-field"
                  value={editing.mrp ?? ""}
                  onChange={(e) => setEditing({ ...editing, mrp: e.target.value === "" ? null : parseFloat(e.target.value) || 0 })}
                  placeholder={editing.category === "Combos" ? "e.g. 675" : "Leave blank to skip"}
                  required={editing.category === "Combos"}
                  data-testid="modal-mrp"
                />
              </div>
              <div><label className="label-field">Weight / size</label><input className="input-field" value={editing.weight || ""} onChange={(e) => setEditing({ ...editing, weight: e.target.value })} data-testid="modal-weight" /></div>
              <div className="flex items-center gap-6 pt-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.in_stock} onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })} className="accent-brand-chili" data-testid="modal-instock" /> In stock</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_bestseller} onChange={(e) => setEditing({ ...editing, is_bestseller: e.target.checked })} className="accent-brand-chili" data-testid="modal-bestseller" /> Bestseller</label>
              </div>
              <div className="sm:col-span-2"><label className="label-field">Description</label><textarea rows={3} className="input-field" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} required data-testid="modal-description" /></div>

              {/* iter70 — URL slug (custom URL for combos; falls back to
                   the SEO_SLUGS map for non-combo products).  Editable
                   for any product but strongly encouraged for Combos so
                   the admin controls the shareable /combos/<slug> URL. */}
              <div className="sm:col-span-2">
                <label className="label-field">
                  URL Slug <span className="text-brand-muted text-xs">{editing.category === "Combos" ? "· required for combos" : "· optional (auto-generated from name if empty)"}</span>
                </label>
                <input
                  className="input-field"
                  value={editing.slug || ""}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") })}
                  placeholder="e.g. andhra-family-pantry-combo"
                  data-testid="modal-slug"
                />
                {editing.slug && (
                  <p className="text-[11px] text-brand-muted mt-1">
                    URL: <span className="font-mono">/{editing.category === "Combos" ? "combos" : "products"}/{editing.slug}</span>
                  </p>
                )}
              </div>

              {/* iter81 — Admin SEO overrides (optional).  When set they
                   win over the SEO_SLUGS entry and auto-generated fallback
                   on the PDP.  Left blank = auto-generated "<name> | Sri
                   Durga Foods" + product description. */}
              <div className="sm:col-span-2 border border-brand-line rounded-xl p-4 bg-brand-cream/40 space-y-3">
                <h4 className="font-serif text-lg text-brand-dark">SEO metadata <span className="text-xs text-brand-muted font-normal">(optional)</span></h4>
                <div>
                  <label className="label-field">SEO Title <span className="text-xs text-brand-muted">(≤ 70 chars)</span></label>
                  <input
                    className="input-field"
                    maxLength={80}
                    value={editing.seo_title || ""}
                    onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })}
                    placeholder={`${editing.name || "Product name"} | Sri Durga Foods`}
                    data-testid="modal-seo-title"
                  />
                </div>
                <div>
                  <label className="label-field">Meta Description <span className="text-xs text-brand-muted">(≤ 160 chars)</span></label>
                  <textarea
                    rows={2}
                    className="input-field"
                    maxLength={180}
                    value={editing.seo_description || ""}
                    onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })}
                    placeholder="Left blank = auto-generated from product description."
                    data-testid="modal-seo-description"
                  />
                </div>
                {/* iter82 — GMC identifiers surfaced in both the product feed
                     and the on-page Product JSON-LD.  Google Shopping
                     strongly prefers at least one of gtin/mpn.  Leaving
                     both blank emits <g:identifier_exists>no</g:> in the
                     feed (still valid, just fewer shopping opportunities). */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-field">GTIN <span className="text-xs text-brand-muted">(optional)</span></label>
                    <input
                      className="input-field"
                      value={editing.gtin || ""}
                      onChange={(e) => setEditing({ ...editing, gtin: e.target.value })}
                      placeholder="EAN/UPC/ISBN"
                      data-testid="modal-gtin"
                    />
                  </div>
                  <div>
                    <label className="label-field">MPN <span className="text-xs text-brand-muted">(optional)</span></label>
                    <input
                      className="input-field"
                      value={editing.mpn || ""}
                      onChange={(e) => setEditing({ ...editing, mpn: e.target.value })}
                      placeholder="Manufacturer part number"
                      data-testid="modal-mpn"
                    />
                  </div>
                </div>
                <div>
                  <label className="label-field">Google Product Category <span className="text-xs text-brand-muted">(overrides category default)</span></label>
                  <input
                    className="input-field"
                    value={editing.google_product_category || ""}
                    onChange={(e) => setEditing({ ...editing, google_product_category: e.target.value })}
                    placeholder="e.g. Food, Beverages & Tobacco > Food Items > Condiments & Sauces"
                    data-testid="modal-gmc-category"
                  />
                </div>
                {/* iter84 — permanent Merchant Center ID for products
                     without variants (combos, single-SKU items).  Read-only
                     under normal use; auto-assigned on save. */}
                <div>
                  <label className="label-field">Merchant Product ID <span className="text-xs text-brand-muted">(permanent · read-only)</span></label>
                  <input
                    className="input-field bg-brand-subtle text-brand-muted font-mono"
                    value={editing.merchant_product_id || "(auto-assigned on save)"}
                    readOnly
                    data-testid="modal-merchant-id"
                    title="Permanent Google Merchant Center product ID. Auto-generated once, never changes on edit."
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="label-field">Main image</label>
                <div className="flex items-center gap-4">
                  {editing.image_url ? (
                    <div className="relative">
                      <img src={editing.image_url} alt="" className="w-20 h-20 rounded-lg object-contain border border-brand-line bg-brand-cream p-1" data-testid="modal-main-image-preview" />
                      <button type="button" onClick={clearMainImage} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-brand-chili text-white flex items-center justify-center shadow" aria-label="Remove main image" data-testid="modal-remove-main-image">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg border border-dashed border-brand-line bg-brand-cream flex items-center justify-center text-xs text-brand-muted">No image</div>
                  )}
                  <label className="btn-secondary cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> {editing.image_url ? "Replace" : "Upload"}
                    <input type="file" accept="image/*" onChange={onUploadMain} className="hidden" data-testid="modal-image-upload" />
                  </label>
                  <button
                    type="button"
                    onClick={() => openPicker({ kind: "main" })}
                    className="btn-secondary"
                    data-testid="modal-pick-cloudinary-main"
                  >
                    <Cloud className="w-4 h-4 mr-2" strokeWidth={1.5} /> Pick from Cloudinary
                  </button>
                  <input className="input-field flex-1" placeholder="or paste URL" value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} data-testid="modal-image-url" />
                </div>
              </div>

              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label-field !mb-0">Additional images (gallery)</label>
                  <label className="text-xs text-brand-chili font-medium cursor-pointer inline-flex items-center" data-testid="modal-add-extra-image">
                    <Plus className="w-3 h-3 mr-1" /> Add image
                    <input type="file" accept="image/*" onChange={onUploadExtra} className="hidden" />
                  </label>
                </div>
                <div className="flex flex-wrap gap-3" data-testid="modal-extra-images">
                  {(editing.images || []).map((img, idx) => (
                    <div key={`${img}-${idx}`} className="relative">
                      <img src={img} alt="" className="w-16 h-16 rounded-lg object-contain border border-brand-line bg-brand-cream p-1" data-testid={`modal-extra-image-${idx}`} />
                      <button type="button" onClick={() => removeExtraImage(idx)} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-brand-chili text-white flex items-center justify-center shadow" aria-label={`Remove image ${idx + 1}`} data-testid={`modal-remove-extra-image-${idx}`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                  {(!editing.images || editing.images.length === 0) && (
                    <span className="text-xs text-brand-muted italic">No additional images. Use the “Add image” button to upload gallery shots.</span>
                  )}
                </div>
              </div>

              {/* iter70 — Products Included editor.  Only shown for the
                   Combos category.  Each row links a real catalog SKU
                   (product_id + variant_label) with a quantity and a
                   snapshot individual_mrp used on the PDP breakdown. */}
              {editing.category === "Combos" && (
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label-field !mb-0">Products Included</label>
                  <button type="button" onClick={addComboItem} className="text-xs text-brand-chili font-medium" data-testid="add-combo-item"><Plus className="w-3 h-3 inline" /> Add product</button>
                </div>
                <p className="text-xs text-brand-muted mb-3">
                  Every combo item is linked to a real catalog product.  Change the product, size, quantity or its snapshot MRP shown on the combo page.
                </p>
                <div className="space-y-3">
                  {(editing.combo_items || []).length === 0 && (
                    <p className="text-sm text-brand-muted italic border border-dashed border-brand-line rounded-xl p-4 text-center" data-testid="combo-items-empty">
                      No products in this combo yet. Click &quot;+ Add product&quot; above to compose it.
                    </p>
                  )}
                  {(editing.combo_items || []).map((ci, i) => {
                    const src = productById[ci.product_id];
                    const availableVariants = (src?.variants || []);
                    return (
                      <div key={`combo-${i}`} className="border border-brand-line rounded-xl p-3 space-y-2" data-testid={`combo-item-row-${i}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            className="input-field flex-1 min-w-[180px]"
                            value={ci.product_id}
                            onChange={(e) => updateComboItem(i, { product_id: e.target.value })}
                            data-testid={`combo-item-product-${i}`}
                          >
                            {nonComboProducts.map((p) => (
                              <option key={p.product_id} value={p.product_id}>{p.name} ({p.category})</option>
                            ))}
                          </select>
                          {availableVariants.length > 0 ? (
                            <select
                              className="input-field w-28"
                              value={ci.variant_label}
                              onChange={(e) => {
                                const label = e.target.value;
                                const v = availableVariants.find((x) => x.label === label);
                                updateComboItem(i, { variant_label: label, individual_mrp: v?.price ?? ci.individual_mrp });
                              }}
                              data-testid={`combo-item-size-${i}`}
                            >
                              {availableVariants.map((v) => <option key={v.label} value={v.label}>{v.label}</option>)}
                            </select>
                          ) : (
                            <span className="text-xs text-brand-muted italic w-28">no sizes</span>
                          )}
                          <input
                            type="number"
                            min="1"
                            step="1"
                            className="input-field w-20"
                            placeholder="Qty"
                            value={ci.quantity}
                            onChange={(e) => updateComboItem(i, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                            data-testid={`combo-item-qty-${i}`}
                          />
                          <input
                            type="number"
                            step="1"
                            className="input-field w-24"
                            placeholder="MRP ₹"
                            value={ci.individual_mrp}
                            onChange={(e) => updateComboItem(i, { individual_mrp: parseFloat(e.target.value) || 0 })}
                            data-testid={`combo-item-mrp-${i}`}
                          />
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => moveComboItem(i, -1)} disabled={i === 0} className="p-1 text-brand-muted hover:text-brand-dark disabled:opacity-30" aria-label="Move up" data-testid={`combo-item-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                            <button type="button" onClick={() => moveComboItem(i, +1)} disabled={i === (editing.combo_items || []).length - 1} className="p-1 text-brand-muted hover:text-brand-dark disabled:opacity-30" aria-label="Move down" data-testid={`combo-item-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                            <button type="button" onClick={() => removeComboItem(i)} className="text-brand-muted hover:text-brand-chili p-1" aria-label="Remove" data-testid={`combo-item-remove-${i}`}><Trash className="w-4 h-4" /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {editing.category === "Combos" && (
              <div className="sm:col-span-2 border-t border-brand-line pt-4 mt-2" data-testid="combo-shipping-section">
                {/* Session K / iter108 — final packed shipping profile
                    for the entire Combo.  These four fields are the
                    outer carton, NOT per-component values.  Cart uses
                    combo_shipping_weight_kg × quantity; Shiprocket uses
                    the L/B/H fields (or falls back to 20×15×10 env). */}
                <label className="label-field">Combo shipping profile (final packed package)</label>
                <p className="text-xs text-brand-muted mb-3">
                  These four values describe the FINAL package the combo actually ships in.
                  They override any component-level dimensions during shipping calculation.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-brand-muted">Weight (kg)</label>
                    <input
                      type="number" step="0.001" min="0"
                      className="input-field"
                      value={editing.combo_shipping_weight_kg ?? ""}
                      onChange={(e) => setEditing({
                        ...editing,
                        combo_shipping_weight_kg: e.target.value === "" ? null : parseFloat(e.target.value),
                      })}
                      placeholder="e.g. 2.500"
                      data-testid="combo-shipping-weight"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-brand-muted">Length (cm)</label>
                    <input
                      type="number" step="0.1" min="0"
                      className="input-field"
                      value={editing.combo_shipping_length_cm ?? ""}
                      onChange={(e) => setEditing({
                        ...editing,
                        combo_shipping_length_cm: e.target.value === "" ? null : parseFloat(e.target.value),
                      })}
                      placeholder="e.g. 30"
                      data-testid="combo-shipping-length"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-brand-muted">Breadth (cm)</label>
                    <input
                      type="number" step="0.1" min="0"
                      className="input-field"
                      value={editing.combo_shipping_breadth_cm ?? ""}
                      onChange={(e) => setEditing({
                        ...editing,
                        combo_shipping_breadth_cm: e.target.value === "" ? null : parseFloat(e.target.value),
                      })}
                      placeholder="e.g. 20"
                      data-testid="combo-shipping-breadth"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-brand-muted">Height (cm)</label>
                    <input
                      type="number" step="0.1" min="0"
                      className="input-field"
                      value={editing.combo_shipping_height_cm ?? ""}
                      onChange={(e) => setEditing({
                        ...editing,
                        combo_shipping_height_cm: e.target.value === "" ? null : parseFloat(e.target.value),
                      })}
                      placeholder="e.g. 15"
                      data-testid="combo-shipping-height"
                    />
                  </div>
                </div>
              </div>
              )}

              {/* iter68 — Variants section is hidden for the Combos
                   category (combos are single-SKU, one price, no size
                   options).  For every other category the section is
                   rendered exactly as before. */}
              {editing.category !== "Combos" && (
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label-field !mb-0">Variants (size + price + per-variant image)</label>
                  <button type="button" onClick={addVariant} className="text-xs text-brand-chili font-medium" data-testid="add-variant"><Plus className="w-3 h-3 inline" /> Add variant</button>
                </div>
                <p className="text-xs text-brand-muted mb-3">Optional per-variant image overrides the main product image when the customer picks this size on the product page.</p>
                <div className="space-y-3">
                  {(editing.variants || []).map((v, i) => (
                    <div key={i} className="border border-brand-line rounded-xl p-3 space-y-2" data-testid={`variant-row-${i}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <input className="input-field flex-1 min-w-[120px]" placeholder="Label e.g. 500g" value={v.label} onChange={(e) => updateVariant(i, { label: e.target.value })} required data-testid={`variant-label-${i}`} />
                        <input type="number" step="1" className="input-field w-24" placeholder="Price" value={v.price} onChange={(e) => updateVariant(i, { price: parseFloat(e.target.value) || 0 })} required data-testid={`variant-price-${i}`} />
                        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={v.available !== false} onChange={(e) => updateVariant(i, { available: e.target.checked })} className="accent-brand-chili" />Avail</label>
                        <button type="button" onClick={() => removeVariant(i)} className="text-brand-muted hover:text-brand-chili p-1" data-testid={`variant-remove-${i}`}><Trash className="w-4 h-4" /></button>
                      </div>
                      <div className="flex items-center gap-3">
                        {v.image_url ? (
                          <div className="relative">
                            <img src={v.image_url} alt="" className="w-14 h-14 rounded-lg object-contain border border-brand-line bg-brand-cream p-1" data-testid={`variant-image-${i}`} />
                            <button type="button" onClick={() => clearVariantImage(i)} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-brand-chili text-white flex items-center justify-center shadow" aria-label="Remove variant image" data-testid={`variant-remove-image-${i}`}><X className="w-2.5 h-2.5" /></button>
                          </div>
                        ) : (
                          <div className="w-14 h-14 rounded-lg border border-dashed border-brand-line bg-brand-cream flex items-center justify-center text-[10px] text-brand-muted">No image</div>
                        )}
                        <label className="text-xs text-brand-chili font-medium cursor-pointer inline-flex items-center">
                          <Upload className="w-3 h-3 mr-1" /> {v.image_url ? "Replace variant image" : "Upload variant image"}
                          <input type="file" accept="image/*" onChange={(e) => onUploadVariant(i, e)} className="hidden" data-testid={`variant-upload-${i}`} />
                        </label>
                        <button
                          type="button"
                          onClick={() => openPicker({ kind: "variant", index: i })}
                          className="text-xs text-brand-dark font-medium cursor-pointer inline-flex items-center hover:text-brand-chili"
                          data-testid={`variant-pick-cloudinary-${i}`}
                        >
                          <Cloud className="w-3 h-3 mr-1" /> Pick
                        </button>
                      </div>
                      {/* iter84 — permanent Merchant Center ID + per-variant GTIN.
                           The Merchant ID is READ-ONLY under normal use because
                           changing it breaks Google Shopping history.
                           iter95 — optional per-variant physical Shipping
                           Weight (kg).  Overrides label-based approximation
                           for liquids/heavy bottles.  Leave blank to keep
                           existing label-parse behaviour. */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-brand-muted">Merchant ID</label>
                          <input
                            className="input-field text-xs bg-brand-subtle text-brand-muted font-mono"
                            value={v.merchant_product_id || "(auto-assigned on save)"}
                            readOnly
                            data-testid={`variant-merchant-id-${i}`}
                            title="Permanent Google Merchant Center product ID. Auto-generated once, never changes."
                          />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-brand-muted">GTIN (optional)</label>
                          <input
                            className="input-field text-xs"
                            value={v.gtin || ""}
                            onChange={(e) => updateVariant(i, { gtin: e.target.value })}
                            placeholder="Barcode / EAN / UPC"
                            data-testid={`variant-gtin-${i}`}
                          />
                        </div>
                        <div>
                          <label
                            className="text-[10px] uppercase tracking-wider text-brand-muted"
                            title="Real packed unit weight in kilograms. Overrides label-based approximation for Shiprocket. Leave blank for label-based default (e.g. 500g → 0.5 kg)."
                          >
                            Shipping weight (kg)
                          </label>
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            className="input-field text-xs"
                            value={
                              v.shipping_weight_kg === null || v.shipping_weight_kg === undefined
                                ? ""
                                : v.shipping_weight_kg
                            }
                            onChange={(e) =>
                              updateVariant(i, {
                                shipping_weight_kg:
                                  e.target.value === "" ? null : Number(e.target.value),
                              })
                            }
                            placeholder="e.g. 1.05 for 1L bottle"
                            data-testid={`variant-shipping-weight-${i}`}
                          />
                        </div>
                      </div>
                      {/* iter102 / Session H — per-variant shipping BOX
                           dimensions (cm) for the individual packed carton.
                           Editable independently for every variant.  Leave
                           blank to fall back to Shiprocket's env-driven
                           default dims (20×15×10).  Multi-item carts are
                           combined via Option A (max-L, max-B, sum-H). */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                        <div>
                          <label
                            className="text-[10px] uppercase tracking-wider text-brand-muted"
                            title="Outer length of the packed individual carton in cm. Used by the Shiprocket rate calculator and shipment payload."
                          >
                            Shipping box length (cm)
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            className="input-field text-xs"
                            value={
                              v.shipping_length_cm === null || v.shipping_length_cm === undefined
                                ? ""
                                : v.shipping_length_cm
                            }
                            onChange={(e) =>
                              updateVariant(i, {
                                shipping_length_cm:
                                  e.target.value === "" ? null : Number(e.target.value),
                              })
                            }
                            placeholder="e.g. 20"
                            data-testid={`variant-shipping-length-${i}`}
                          />
                        </div>
                        <div>
                          <label
                            className="text-[10px] uppercase tracking-wider text-brand-muted"
                            title="Outer breadth / width of the packed individual carton in cm."
                          >
                            Shipping box breadth (cm)
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            className="input-field text-xs"
                            value={
                              v.shipping_breadth_cm === null || v.shipping_breadth_cm === undefined
                                ? ""
                                : v.shipping_breadth_cm
                            }
                            onChange={(e) =>
                              updateVariant(i, {
                                shipping_breadth_cm:
                                  e.target.value === "" ? null : Number(e.target.value),
                              })
                            }
                            placeholder="e.g. 10"
                            data-testid={`variant-shipping-breadth-${i}`}
                          />
                        </div>
                        <div>
                          <label
                            className="text-[10px] uppercase tracking-wider text-brand-muted"
                            title="Outer height of the packed individual carton in cm."
                          >
                            Shipping box height (cm)
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            className="input-field text-xs"
                            value={
                              v.shipping_height_cm === null || v.shipping_height_cm === undefined
                                ? ""
                                : v.shipping_height_cm
                            }
                            onChange={(e) =>
                              updateVariant(i, {
                                shipping_height_cm:
                                  e.target.value === "" ? null : Number(e.target.value),
                              })
                            }
                            placeholder="e.g. 10"
                            data-testid={`variant-shipping-height-${i}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </form>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-brand-line shrink-0">
              <button type="button" onClick={() => setEditing(null)} className="btn-secondary" disabled={uploadBusy}>Cancel</button>
              <button type="submit" form="product-form" className="btn-primary" disabled={uploadBusy} data-testid="modal-save">{uploadBusy ? "Uploading…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {/* iter40 — Cloudinary image picker (admin-only) */}
      <CloudinaryImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={onPickFromCloudinary}
        defaultFolder={editing?.category ? `Sri Durga Foods/${editing.category}` : ""}
      />
    </div>
  );
}
